import {useQuery} from "@tanstack/react-query";
import React, {useMemo, useState} from "react";
import Statement from "../../components/Statements/Statement.tsx";
import {AccessTime, Assistant, SentimentVeryDissatisfied} from "@mui/icons-material";
import {Box, ChipProps, Container, Stack, Typography} from "@mui/material";
import {CardGrid, ChipLine, Expandable, ImageHeader, StandardCard} from "@laser-project/kyui";
import {ICalibration} from "../../types/models/Calibration.ts";
import CalibrationRest from "../../rest/CalibrationRest.ts";
import ImageRest from "../../rest/ImageRest.ts";
import JobRest from "../../rest/JobRest.ts";
import {IPopulatedCutter, IPopulatedJob} from "../../types/FrontendTypes.ts";
import CalibrationCardPlaceholderImage from "../../assets/calibrationIcons/calibrationCardPlaceholder.jpg";
import CutterRest from "../../rest/CutterRest.ts";
import {getCutterStatusChips} from "../../components/StatusChips/CutterStatusChips.tsx";
import {getJobStatusChips} from "../../components/StatusChips/JobStatusChips.tsx";
import moment from "moment";
import Boxel from "../../components/Icons/Boxel.tsx";
import {MeasurementIcon} from "../../components/Calibrations/MeasurementIcon.tsx";
import {MeasurementDialog} from "../../components/Calibrations/Measurements/MeasurementDialog.tsx";
import {MeasurementImage} from "../../components/Calibrations/MeasurementImage.tsx";

function Calibration({calibrationId}) {

    const calibrationRest = useMemo(() => new CalibrationRest(), []);
    const jobRest = useMemo(() => new JobRest(), []);
    const cutterRest = useMemo(() => new CutterRest(), []);
    const imageRest = useMemo(() => new ImageRest(), []);

    const [open, setOpen] = useState({});

    const {error, data, refetch} = useQuery({
        queryKey: ['calibrations', calibrationId],
        queryFn: () =>
            calibrationRest.get_calibrations({_id: calibrationId} as Partial<ICalibration>).then(res => {
                console.log("Calibration data:", res.data)
                return res.data.data[0] as ICalibration;
            })
    })

    const job = useQuery({
        queryKey: ['jobs', data ? data.jobId ?? null : null],
        queryFn: () => data.jobId ? jobRest.getById(data.jobId).then(res =>
            res.data.data as IPopulatedJob) : null,
    })

    const job_image = useQuery({
        queryKey: ['images', job.data ? job.data.imageId : null],
        queryFn: () => job.data ? imageRest.getUrlById(job.data.imageId) : null,
    });

    const cutter = useQuery({
        queryKey: ['cutters', data ? data.cutterId : null],
        queryFn: () => data ? cutterRest.getById(data.cutterId)
            .then(res => res.data.data as IPopulatedCutter) : null,
    });

    const jobsbyCalibration = useQuery({
        queryKey: ['jobs/byCalibrationId', data ? data._id : null],
        queryFn: () => data ? jobRest.getByCalibrationId(data._id).then(res => res.data.data) : null,
    });

    if (error) {
        return <Statement message={error.message} icon={<SentimentVeryDissatisfied/>}/>
    }

    if (!data) {
        return <Statement message={"Loading..."} icon={<SentimentVeryDissatisfied/>}/>
    }

    return (
        <Box sx={{width: '100%', overFlowY: ""}}>
            <Container sx={{p: 4}}>
                <Stack rowGap={3}>
                    <ImageHeader
                        images={[CalibrationCardPlaceholderImage]}
                        isLoading={!data}
                        menuActions={[
                            {
                                label: "Delete",
                                onClick: () => calibrationRest.delete(data._id).then(() => refetch())
                            },
                        ]}
                        actions={[]}
                    />
                    <Stack spacing={1}>
                        <Typography variant={"h1"}>
                            {
                                "⚙ " + (job.data ? job.data.title : "-")
                            }
                        </Typography>
                        <ChipLine
                            chips={[
                                {
                                    id: 'CreatedAt',
                                    label: "Created: " + moment(data.createdAt).fromNow(),
                                    props: {
                                        variant: "outlined",
                                        icon: <AccessTime color={"primary"}/>,
                                    }
                                },
                                {
                                    id: 'Id',
                                    label: "Id: " + (data ? data._id : "-"),
                                }
                            ]}
                        />
                    </Stack>
                    <CardGrid tilesPerRow={3} cards={[
                        <StandardCard
                            title={cutter.data ? cutter.data.name : "Cutter unknown"}
                            chips={cutter.data ? getCutterStatusChips(cutter.data) : []}
                            type={"model"}
                            image={cutter.data?.imageId ? imageRest.getUrlById(cutter.data?.imageId) : undefined}
                            fallbackIcon={<Boxel/>}
                            imageAlt={"Cutter"}/>,
                        <StandardCard
                            title={data.sheetId ?? "Sheet unknown"}
                            type={"model"}
                            image={null}
                            fallbackIcon={<Boxel/>}
                            imageAlt={"Sheet"}/>,
                        <StandardCard
                            title={job.data ? job.data.title : "Job unknown"}
                            chips={job.data ? getJobStatusChips(job.data) : []}
                            type={"model"}
                            image={job_image.data}
                            fallbackIcon={<Boxel/>}
                            imageAlt={"Job"}/>,
                    ]
                    }/>

                    <Expandable title={"Jobs"} subtitle={""}>
                        <CardGrid cards={jobsbyCalibration.data?.map(j => {
                            // console.log(j)
                            return (
                                <StandardCard
                                    title={j.title}
                                    chips={[
                                        ...(j._id == data.jobId ? [{
                                            label: "origin",
                                            props: {
                                                variant: "outlined",
                                                icon: <Assistant color={"secondary"}/>,
                                            } as ChipProps
                                        }] : []),
                                        ...getJobStatusChips(j)]}
                                    type={"model"}
                                    image={
                                        j.imageId ? imageRest.getUrlById(j.imageId) : undefined
                                    }
                                    imageAlt={"Job"}
                                    onClick={() => {
                                        window.location.href = "/job/" + j._id;
                                    }}
                                />
                            )
                        })}/>
                    </Expandable>

                    <Expandable title={"Measurements"} subtitle={""}>
                        {data.measurements.length == 0 ?
                            <Statement message={"No measurements found"} icon={<Boxel/>}/> :
                            <CardGrid cards={
                                data.measurements
                                    .filter(m => m.type !== "Kerf") // Currently kerf is excluded
                                    .map(m => {
                                    const isObject = typeof m.value === 'object' && m.value !== null;
                                    return (
                                        <>
                                            <MeasurementDialog
                                            handleClose={() => setOpen(() => ({...open, [m._id]: false}))}
                                            open={open[m._id] ?? false}
                                            measurement={m}
                                            />
                                            <StandardCard
                                                title={m.type}
                                                chips={
                                                    !isObject ? [
                                                        {
                                                            label: m.value.toString(),
                                                            props: {
                                                                variant: "outlined",
                                                            } as ChipProps
                                                        },
                                                    ] : []
                                                }
                                                type={"model"}
                                                image={MeasurementImage(m.type)}
                                                fallbackIcon={<MeasurementIcon icon={m.type}/>}
                                                imageAlt={"Measurement"}
                                                onClick={() => {
                                                    setOpen(() => ({...open, [m._id]: true}));
                                                }}
                                            />
                                        </>
                                    )}
                                )}
                            />
                        }
                    </Expandable>

                </Stack>
            </Container>
        </Box>
    )
}

export default Calibration